"use client"

import React from 'react'
import {
    FormField,
} from '../ui/form'
import {
    Input,
    InputProps,
} from '../ui/new-input'
import {
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    Control,
} from 'react-hook-form'
import { cn } from '../../lib/utils'
import FormTextInputWrapper, { InputGroupProps } from './form-input-wrapper'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormIntegerInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormIntegerInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<InputProps, 'type'> & {
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps,
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>
}

/**
 * A form integer input component that prevents decimal input
 * 
 * This component renders a number input that only accepts whole numbers (integers).
 * It prevents users from entering decimal points and automatically converts valid input to integers.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.variant - Input variant styling
 * @param props.size - Input size
 * @param props.inputProps - Additional input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormIntegerInput
 *   name="numChildren"
 *   control={form.control}
 *   placeholder="Enter number of children"
 *   inputGroupProps={{
 *     label: "Number of Children",
 *   }}
 * />
 * ```
 * 
 * @throws {Error} When the name prop is not provided
 * 
 * @returns JSX.Element - The rendered integer input component
 */
function FormIntegerInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    inputGroupProps,
    name,
    control,
    variant,
    size,
    ...inputProps
}: FormIntegerInputProps<TFieldValues, TName>) {
    // Validate that name is provided as it's required for form integration
    if (!name) {
        throw new Error('name is required')
    }

    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    /**
     * Handles input value changes with integer-specific processing
     * 
     * @param e - Input change event
     * @param field - Form field render props
     */
    const onChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        field: ControllerRenderProps<TFieldValues, TName>
    ) => {
        if (isReadOnly) return
        const value = e.target.value
        
        // Allow empty string for clearing the field
        if (value === '') {
            field.onChange('')
            return
        }
        
        // Remove any non-digit characters (including decimal points)
        const integerValue = value.replace(/[^0-9]/g, '')
        
        // Only update if the cleaned value is different from the original
        if (integerValue !== value) {
            // Update the input value to show only digits
            e.target.value = integerValue
        }
        
        // Convert to number if not empty, otherwise keep as empty string
        if (integerValue === '') {
            field.onChange('')
        } else {
            field.onChange(Number(integerValue))
        }
    }

    /**
     * Handles key press events to prevent decimal point and other non-integer characters
     * 
     * @param e - Keyboard event
     */
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
        if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            (e.keyCode === 90 && e.ctrlKey === true)) {
            return
        }
        
        // Prevent decimal point, plus, minus, and any non-numeric characters
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
            e.preventDefault()
        }
    }

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => {
                const errorMessage = fieldState.error?.message
                return (
                    <FormTextInputWrapper
                        inputGroupProps={{
                            ...inputGroupProps,
                            isDirty: isDirty,
                        }}
                    >
                        <Input
                            {...inputProps}
                            {...field}
                            onChange={(e) => onChange(e, field)}
                            onKeyDown={onKeyDown}
                            value={field.value || ""}
                            variant={!errorMessage ? variant : "destructive"}
                            size={size}
                            disabled={isReadOnly || inputProps.disabled}
                            className={cn(
                                "transition-colors duration-200",
                                isDirty && "border-[#9c27b0] focus:border-[#9c27b0] focus:ring-[#9c27b0]/20 shadow-sm",
                                inputProps.className
                            )}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                        />
                    </FormTextInputWrapper>
                )
            }}
        />
    )
}

export default FormIntegerInput
